import React, { useEffect, useState } from "react";
import { MapPin, Clock, Users, CheckCircle, X, Phone, Mail, Navigation, Utensils, Car, Ticket, ChevronLeft, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Trees, Baby, Fish, Camera } from 'lucide-react';

const BangaloreCityTour = () => {
    const navigate = useNavigate();
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState(null);
    const [currentIndex, setCurrentIndex] = useState(0);

    const attractions = [
        {
            id: 1,
            name: "Bangalore Palace",
            time: "10:00 – 10:45 AM",
            duration: "45 minutes",
            image: "/assets/banglore-place.jpeg",
            description: "Begin the city tour with a visit to Bangalore Palace, a 19th-century royal landmark known for its grand architecture and heritage charm.",
            highlights: [
                "Royal palace spread across a vast campus",
                "Elegant interiors with wooden carvings and classic artwork",
                "Watch towers, turrets, and heritage architecture",
                "Beautifully maintained gardens and palace exteriors",
                "Imported artifacts and historic furnishings"
            ],
            facts: {
                style: "Tudor architecture",
                started: "April 1874",
                owner: "Pramoda Devi Wadiyar",
                opened: "1944"
            }
        },
        {
            id: 2,
            name: "Lalbagh Botanical Garden",
            time: "11:00 AM – 12:30 PM",
            duration: "1.5 hours",
            image: "/assets/Lalbagh-Bangalore_2.jpg",
            description: "One of Bangalore's most iconic green spaces, known for its rich plant collection, scenic landscapes, and peaceful atmosphere.",
            highlights: [
                "Famous Glass House inspired by London's Crystal Palace",
                "Beautiful Lalbagh Lake with walking paths",
                "Rare tropical and subtropical plants",
                "Historic Kempegowda Watchtower",
                "Flower Clock, Bonsai Garden, and themed gardens",
                "Seasonal attractions like the Lalbagh Flower Show"
            ]
        },
        {
            id: 3,
            name: "Cubbon Park Activities",
            time: "02:00 – 03:30 PM",
            duration: "1.5 hours",
            image: "/assets/cubbon-park.jpg",
            description: "A large peaceful green space in the heart of Bangalore with multiple attractions.",
            highlights: [
                "Jawahar Bal Bhavan – 45 minutes (Toy Train, Boating, Adventure rides)",
                "Namma Bengaluru Aquarium – 45 minutes (Exotic fish species)"
            ]
        },
        {
            id: 4,
            name: "Visvesvaraya Industrial & Technological Museum",
            time: "03:30 – 06:00 PM",
            duration: "2.5 hours",
            image: "/assets/visvesvaraya-industrial.jpg",
            description: "One of Bangalore's most popular science museums dedicated to innovation, technology, and learning. Experience interactive exhibits and fascinating special shows throughout the day.",
            highlights: [
                "Science on a Sphere (SOS) - 20-minute shows at Ground Floor (11:15 AM, 12:15 PM, 03:15 PM, 04:15 PM)",
                "Taramandal (Planetarium) - 20-minute shows at Ground Floor (11:45 AM, 03:00 PM)",
                "Science Show - 20-minute demonstrations at Ground Floor (12:30 PM, 03:30 PM)",
                "3D Show - 20-minute shows at First Floor Fun Science Gallery (multiple times daily)",
                "Wright Brothers Flight Simulator - 3-minute experience at Engine Hall, Basement Floor (10:00 AM to 05:30 PM)",
                "Interactive science and technology exhibits across multiple galleries",
                "Hands-on learning experiences for all ages",
                "Engaging galleries on engineering, space, and innovation"
            ],
            facts: {
                established: "1962",
                operatingHours: "10:00 AM - 05:30 PM",
                specialShows: "First come, first serve",
                type: "Ministry of Culture, Govt. of India"
            }
        },
        {
            id: 5,
            name: "Vidhana Soudha",
            time: "06:30 PM",
            duration: "Photo stop",
            image: "/assets/vidhan.jpg",
            description: "One of Bangalore's most iconic landmarks and the seat of the Karnataka State Legislature.",
            highlights: [
                "Impressive Neo-Dravidian architecture",
                "Massive granite structure with grand central dome",
                "Popular photo opportunity"
            ]
        }
    ];

    const schedule = [
        { time: "07:00 AM", activity: "Pickup from Bellandur", icon: Navigation },
        { time: "07:15 AM", activity: "Marathahalli (Kalamandir)", icon: Navigation },
        { time: "07:30 AM", activity: "Pickup from Spice Garden, Marathahalli", icon: Navigation },
        { time: "08:30 – 09:30 AM", activity: "Breakfast Break", icon: Utensils },
        { time: "10:00 – 10:45 AM", activity: "Bangalore Palace", icon: MapPin },
        { time: "11:00 AM – 12:30 PM", activity: "Lalbagh Botanical Garden", icon: MapPin },
        { time: "01:00 – 02:00 PM", activity: "Lunch Break (Own expense)", icon: Utensils },
        { time: "02:00 – 03:30 PM", activity: "Cubbon Park Area Activities", icon: Trees },
        { time: "02:00 – 02:45 PM", activity: "Jawahar Bal Bhavan", icon: Baby },
        { time: "02:45 – 03:30 PM", activity: "Namma Bengaluru Aquarium", icon: Fish },
        { time: "03:30 – 06:00 PM", activity: "Visvesvaraya Museum", icon: MapPin },
        { time: "06:30 PM", activity: "Vidhana Soudha Photo Stop", icon: Camera }
    ];

    const includes = [
        "Entry tickets for all attractions",
        "Breakfast included",
        "AC transportation throughout",
        "Trip coordinator",
        "All parking and toll charges",
    ];

    const excludes = [
        "Lunch (own expense)",
        "Personal expenses",
        "Any items not mentioned in inclusions",
    ];

    const nextSlide = () => {
        setCurrentIndex((prev) => (prev + 1) % attractions.length);
    };

    const prevSlide = () => {
        setCurrentIndex((prev) => (prev - 1 + attractions.length) % attractions.length);
    };

    useEffect(() => {
        const token = localStorage.getItem('authToken');
        const userData = localStorage.getItem('userData');

        if (token && userData) {
            setUser(JSON.parse(userData));
            setIsAuthenticated(true);
        }
    }, []);

    const handleBookNow = () => {
        navigate('/booking')
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-orange-50 via-yellow-50 to-red-50">
            {/* Quick Info Bar */}
            <div className="bg-white shadow-md p-1 sticky top-0 z-[99]">
                <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
                    <div className="flex flex-wrap justify-center sm:justify-between items-center gap-2 sm:gap-4">
                        <div className="hidden sm:flex items-center gap-2 text-gray-700 text-sm">
                            <Ticket className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" />
                            <span className="font-semibold">All Tickets Included</span>
                        </div>
                        <div className="hidden sm:flex items-center gap-2 text-gray-700 text-sm">
                            <Utensils className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" />
                            <span className="font-semibold">Breakfast Included</span>
                        </div>
                        <div className="hidden sm:flex items-center gap-2 text-gray-700 text-sm">
                            <Car className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" />
                            <span className="font-semibold">AC Transport</span>
                        </div>
                        <button
                            onClick={handleBookNow}
                            className="bg-orange-600 text-white px-4 sm:px-6 py-2 rounded-full text-sm sm:text-base font-semibold hover:bg-orange-700 transition-colors w-full sm:w-auto"
                        >
                            ₹2,199 - Book Now
                        </button>
                    </div>
                </div>
            </div>

            <div className="w-full mx-auto px-0 sm:px-4 py-6 sm:py-12">
                <section className="mb-8 sm:mb-16 px-4">
                    <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 sm:mb-8 text-center">
                        Complete Day Schedule
                    </h2>
                    <div className="sm:bg-white sm:rounded-2xl sm:rounded-3xl sm:shadow-xl sm:p-4 sm:p-6 lg:p-8">
                        {/* Horizontal Scrollable Container */}
                        <div className="overflow-x-auto overflow-y-visible pb-4 px-4 sm:mx-0 sm:px-0">
                            <div className="flex gap-3 sm:gap-4 w-max">
                                {schedule.map((item, index) => {
                                    const Icon = item.icon;
                                    return (
                                        <div
                                            key={index}
                                            className="relative flex flex-col items-center gap-2 sm:gap-3 p-3 sm:p-4 rounded-xl hover:bg-orange-50 transition-all hover:shadow-lg border border-gray-200 w-[160px] sm:w-[200px] lg:w-[220px] bg-white flex-shrink-0"
                                        >
                                            <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-orange-500 to-red-500 rounded-full flex items-center justify-center text-white shadow-md">
                                                <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
                                            </div>

                                            <div className="font-bold text-orange-600 text-xs sm:text-sm text-center leading-tight">
                                                {item.time}
                                            </div>

                                            <div className="text-gray-800 font-medium text-xs sm:text-sm text-center leading-snug px-1">
                                                {item.activity}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Scroll Hint */}
                        <div className="text-center text-gray-400 text-[11px] sm:text-sm mt-3 px-4 sm:px-0">
                            ← Scroll horizontally to view complete schedule →
                        </div>
                    </div>
                </section>

                <style jsx>{`
        .overflow-x-auto {
            -webkit-overflow-scrolling: touch;
        }
        .overflow-x-auto::-webkit-scrollbar {
            height: 6px;
        }
        .overflow-x-auto::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 10px;
        }
        .overflow-x-auto::-webkit-scrollbar-thumb {
            background: #ffa500;
            border-radius: 10px;
        }
        .overflow-x-auto::-webkit-scrollbar-thumb:hover {
            background: #ff8c00;
        }
        
        @media (max-width: 640px) {
            .overflow-x-auto::-webkit-scrollbar {
                display: none;
            }
            .overflow-x-auto {
                -ms-overflow-style: none;
                scrollbar-width: none;
            }
        }
    `}</style>

                {/* Attractions */}
                <section className="mb-8 sm:mb-16 px-4 sm:px-0">
                    <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 sm:mb-8 text-center">
                        Attractions Covered
                    </h2>

                    {/* Mobile Swiper */}
                    <div className="relative sm:hidden mb-6">
                        <div className="overflow-hidden">
                            <div
                                className="flex transition-transform duration-300 ease-in-out"
                                style={{ transform: `translateX(-${currentIndex * 100}%)` }}
                            >
                                {attractions.map((attraction, index) => (
                                    <div
                                        key={attraction.id}
                                        className="w-full flex-shrink-0 px-0"
                                    >
                                        <div className="bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col h-[600px]">
                                            <div
                                                style={{
                                                    backgroundImage: `linear-gradient(rgba(249, 115, 22, 0.7), rgba(239, 68, 68, 0.7)), url(${attraction.image})`,
                                                    backgroundSize: 'cover',
                                                    backgroundPosition: 'center',
                                                }}
                                                className="text-white p-4 min-h-[140px] flex items-end flex-shrink-0"
                                            >
                                                <div className="w-full">
                                                    <div className="text-xs opacity-90 mb-1">Stop {index + 1}</div>
                                                    <h3 className="text-xl font-bold mb-2">{attraction.name}</h3>
                                                    <div className="flex flex-wrap items-center gap-3 text-xs">
                                                        <div className="flex items-center gap-1">
                                                            <Clock className="w-3 h-3" />
                                                            <span className="truncate">{attraction.time}</span>
                                                        </div>
                                                        <div className="flex items-center gap-1">
                                                            <Users className="w-3 h-3" />
                                                            {attraction.duration}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="p-4 flex-1 flex flex-col overflow-y-auto">
                                                <p className="text-gray-700 mb-4 leading-relaxed text-sm">
                                                    {attraction.description}
                                                </p>

                                                <div className="mb-4">
                                                    <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2 text-sm">
                                                        <CheckCircle className="w-4 h-4 text-green-600" />
                                                        Highlights
                                                    </h4>
                                                    <div className="grid grid-cols-1 gap-2">
                                                        {attraction.highlights.map((highlight, idx) => (
                                                            <div key={idx} className="flex items-start gap-2">
                                                                <div className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-1.5 flex-shrink-0"></div>
                                                                <span className="text-gray-700 text-xs">{highlight}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>

                                                {attraction.facts && (
                                                    <div className="bg-orange-50 rounded-xl p-3 mt-auto">
                                                        <h4 className="font-bold text-gray-900 mb-2 text-sm">Quick Facts</h4>
                                                        <div className="grid grid-cols-2 gap-3">
                                                            {Object.entries(attraction.facts).map(([key, value]) => (
                                                                <div key={key}>
                                                                    <div className="text-xs text-gray-600 capitalize mb-1">
                                                                        {key.replace(/([A-Z])/g, ' $1').trim()}
                                                                    </div>
                                                                    <div className="font-semibold text-gray-800 text-xs">{value}</div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Navigation Buttons */}
                        <button
                            onClick={prevSlide}
                            disabled={currentIndex === 0}
                            className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/95 backdrop-blur-sm p-2 rounded-full shadow-lg z-10 disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                            <ChevronLeft className="w-5 h-5 text-gray-800" />
                        </button>
                        <button
                            onClick={nextSlide}
                            disabled={currentIndex === attractions.length - 1}
                            className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/95 backdrop-blur-sm p-2 rounded-full shadow-lg z-10 disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                            <ChevronRight className="w-5 h-5 text-gray-800" />
                        </button>

                        {/* Dots Indicator */}
                        <div className="flex justify-center gap-2 mt-4">
                            {attractions.map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => setCurrentIndex(index)}
                                    className={`h-2 rounded-full transition-all ${index === currentIndex
                                        ? 'bg-orange-500 w-8'
                                        : 'bg-gray-300 w-2'
                                        }`}
                                    aria-label={`Go to slide ${index + 1}`}
                                />
                            ))}
                        </div>

                        {/* Card Counter */}
                        <div className="text-center mt-2 text-sm text-gray-600 font-medium">
                            {currentIndex + 1} / {attractions.length}
                        </div>
                    </div>

                    {/* Desktop List */}
                    <div className="hidden sm:block space-y-8 px-4">
                        {attractions.map((attraction, index) => (
                            <div
                                key={attraction.id}
                                className="bg-white rounded-3xl shadow-xl overflow-hidden hover:shadow-2xl transition-shadow"
                            >
                                <div
                                    style={{
                                        backgroundImage: `linear-gradient(rgba(249, 115, 22, 0.7), rgba(239, 68, 68, 0.7)), url(${attraction.image})`,
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center',
                                    }}
                                    className="text-white p-6 min-h-[200px] flex items-end"
                                >
                                    <div className="w-full">
                                        <div className="text-sm opacity-90 mb-1">Stop {index + 1}</div>
                                        <h3 className="text-2xl font-bold mb-2">{attraction.name}</h3>
                                        <div className="flex flex-wrap items-center gap-4 text-sm">
                                            <div className="flex items-center gap-1">
                                                <Clock className="w-4 h-4" />
                                                <span className="truncate">{attraction.time}</span>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <Users className="w-4 h-4" />
                                                {attraction.duration}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="p-6">
                                    <p className="text-gray-700 mb-6 leading-relaxed">
                                        {attraction.description}
                                    </p>

                                    <div className="mb-6">
                                        <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                                            <CheckCircle className="w-5 h-5 text-green-600" />
                                            Highlights
                                        </h4>
                                        <div className="grid grid-cols-2 gap-3">
                                            {attraction.highlights.map((highlight, idx) => (
                                                <div key={idx} className="flex items-start gap-2">
                                                    <div className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2 flex-shrink-0"></div>
                                                    <span className="text-gray-700 text-sm">{highlight}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {attraction.facts && (
                                        <div className="bg-orange-50 rounded-xl p-4">
                                            <h4 className="font-bold text-gray-900 mb-3">Quick Facts</h4>
                                            <div className="grid grid-cols-4 gap-4">
                                                {Object.entries(attraction.facts).map(([key, value]) => (
                                                    <div key={key}>
                                                        <div className="text-xs text-gray-600 capitalize mb-1">
                                                            {key.replace(/([A-Z])/g, ' $1').trim()}
                                                        </div>
                                                        <div className="font-semibold text-gray-800 text-sm">{value}</div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Includes & Excludes */}
                <section className="mb-8 sm:mb-16 px-4 sm:px-0">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
                        <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl p-6 sm:p-8">
                            <h3 className="text-xl sm:text-2xl font-bold text-green-700 mb-4 sm:mb-6 flex items-center gap-2">
                                <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6" />
                                What's Included
                            </h3>
                            <ul className="space-y-2 sm:space-y-3">
                                {includes.map((item, idx) => (
                                    <li key={idx} className="flex items-start gap-2 sm:gap-3">
                                        <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 flex-shrink-0 mt-0.5" />
                                        <span className="text-gray-700 text-sm sm:text-base">{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl p-6 sm:p-8">
                            <h3 className="text-xl sm:text-2xl font-bold text-red-700 mb-4 sm:mb-6 flex items-center gap-2">
                                <X className="w-5 h-5 sm:w-6 sm:h-6" />
                                Not Included
                            </h3>
                            <ul className="space-y-2 sm:space-y-3">
                                {excludes.map((item, idx) => (
                                    <li key={idx} className="flex items-start gap-2 sm:gap-3">
                                        <X className="w-4 h-4 sm:w-5 sm:h-5 text-red-600 flex-shrink-0 mt-0.5" />
                                        <span className="text-gray-700 text-sm sm:text-base">{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </section>

                {/* Pickup Locations */}
                <section className="mb-8 sm:mb-16 px-4 sm:px-0">
                    <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl p-6 sm:p-8">
                        <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6 flex items-center gap-2">
                            <Navigation className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" />
                            Pickup Locations
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                            <div className="bg-orange-50 rounded-xl p-4 sm:p-6">
                                <div className="font-bold text-base sm:text-lg text-gray-900 mb-2">Bellandur</div>
                                <div className="text-orange-600 font-semibold text-sm sm:text-base">07:00 AM</div>
                            </div>
                            <div className="bg-orange-50 rounded-xl p-4 sm:p-6">
                                <div className="font-bold text-base sm:text-lg text-gray-900 mb-2">Marathahalli (Kalamandir)</div>
                                <div className="text-orange-600 font-semibold text-sm sm:text-base">07:15 AM</div>
                            </div>
                            <div className="bg-orange-50 rounded-xl p-4 sm:p-6">
                                <div className="font-bold text-base sm:text-lg text-gray-900 mb-2">Spice Garden, Marathahalli</div>
                                <div className="text-orange-600 font-semibold text-sm sm:text-base">07:30 AM</div>
                            </div>
                            <div className="bg-orange-50 rounded-xl p-4 sm:p-6">
                                <div className="font-bold text-base sm:text-lg text-gray-900 mb-2">Other</div>
                            </div>
                        </div>
                        <p className="text-xs sm:text-sm text-gray-600 mt-3 sm:mt-4">
                            *Timings may vary slightly based on traffic conditions
                        </p>
                    </div>
                </section>

                {/* Pricing */}
                <section className="mb-8 sm:mb-16 px-4 sm:px-0">
                    <div className="bg-gradient-to-r from-orange-600 to-red-600 rounded-2xl sm:rounded-3xl shadow-xl p-6 sm:p-8 text-white text-center">
                        <h3 className="text-2xl sm:text-3xl font-bold mb-3 sm:mb-4">Tour Package Price</h3>
                        <div className="text-4xl sm:text-6xl font-bold mb-2">₹2,199</div>
                        <div className="text-lg sm:text-xl opacity-90 mb-4 sm:mb-6">per person</div>
                        <div className="text-base sm:text-lg opacity-90 mb-6 sm:mb-8">
                            (Including all taxes & entry tickets)
                        </div>
                        <button
                            onClick={handleBookNow}
                            className="bg-white text-orange-600 px-8 sm:px-12 py-3 sm:py-4 rounded-full font-bold text-lg sm:text-xl hover:bg-orange-50 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1 w-full sm:w-auto"
                        >
                            Book This Tour Now
                        </button>
                    </div>
                </section>
            </div>

            {/* Contact Footer */}
            <div className="bg-gray-900 text-white py-8 sm:py-12">
                <div className="max-w-7xl mx-auto px-3 sm:px-4 text-center">
                    <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4">Need Help?</h3>
                    <p className="text-gray-400 mb-4 sm:mb-6 text-sm sm:text-base">Contact our team for any queries</p>
                    <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-4 sm:gap-6">
                        <a href="tel:+919876543210" className="flex items-center justify-center gap-2 hover:text-orange-400 transition-colors text-sm sm:text-base">
                            <Phone className="w-4 h-4 sm:w-5 sm:h-5" />
                            <span>+91 98765 43210</span>
                        </a>
                        <a href="mailto:info@bangaloretour.com" className="flex items-center justify-center gap-2 hover:text-orange-400 transition-colors text-sm sm:text-base">
                            <Mail className="w-4 h-4 sm:w-5 sm:h-5" />
                            <span>info@bangaloretour.com</span>
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BangaloreCityTour;